import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  ThemeProvider,
  Stack,
  Alert,
  Snackbar,
  Typography,
  Fab,
  useTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { RenderTable } from "../../common/splitview-table/RenderTable";
import DropdownEditor from "../DropdownlEditor";
import { Approver } from "../../../types/approveListReponse";
import { ApproverGetResponse } from "../../../types/getApproverResponse";
import { AdminRequestPayload } from "../../../types/adminRequestPayload";
import { deleteAdminApi, submitAdminApi } from "../../../api/submitAdminApi";
import { ADMIN_TEMPLATE_HEADERS } from "../../../constants/Admin";
import { getApproverListApi } from "../../../api/getApproverListApi";
import {
  getAdminApi,
  getMaterialTypeApi,
  getPlantsApi,
} from "../../../api/getAdminApi";
import ErrorDialog from "../../common/ErrorDialog/ErrorDialog";
import { gridTheme } from "../../../utils/tableUtils";
import ButtonComponent from "../../common/button/Button";
import UploadDialogBox from "../../common/uploadDialogBox/UploadDialogBox";
import { submitAdminBulkApi } from "../../../api/submitAdminBulkApi";
import Download from "../../common/DownloadGrid/Download";
import { set } from "date-fns";
import { getApproverHistoryById } from "../../../api/adminGetApproverHistory";
import HistoryLogDialog, { HistoryLogTableRow } from "./HistoryLogDialog";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { setApiCall } from "../../../redux/actions/userSlice";
import { hasRolePermission } from "../../../constants/roleAccess";
import BulkPasteFilter from "../../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../../../components/common/FilterVariant/FilterManagerButton";
// Table row type for AG Grid (different from API type)
interface AdminTableRow {
  __uid?: string; // internal unique identifier for AG Grid
  id: string; // unique identifier
  adminId?: number | string;
  Plant: string | number;
  "IO Material Type": string;
  "Planning Lead": string;
  Approver: string;
  "Approver Email Address": string;
  "Updated By"?: string;
  "Updated Timestamp"?: string;
  isNew?: boolean; // flag for new rows
  isEditing?: boolean; //  ADD THIS
}

export const AdminView = () => {
  const theme = useTheme();
  const { accounts } = useMsal();
  const gridRef = useRef<any>(null);
  const dispatch = useDispatch();
  const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [adminList, setAdminList] = useState<ApproverGetResponse>([]);
  const [tableRows, setTableRows] = useState<AdminTableRow[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [uploadPayload, setUploadPayload] = useState<AdminRequestPayload[]>([]);
  const [toastSeverity, setToastSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [openDialog, setOpenDialog] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState<AdminTableRow | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<HistoryLogTableRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<AdminTableRow | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [showApproverInfo, setShowApproverInfo] = useState(false);

  const newRowIdRef = useRef(0); // For unique IDs of new rows
  const historyAbortRef = useRef<AbortController | null>(null);

  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);
  const [onDeleteRow, setOnDeleteRow] = useState<AdminTableRow>();
  const [action, setAction] = useState<string>("submit");
  const [plants, setPlants] = useState<string[]>([]);
  const [materialTypes, setMaterialTypes] = useState<string[]>([]);
  const selectedPlant = useSelector(
    (state: any) => state.userRoles.selectedPlant || [],
  );
  const currentRole = useSelector(
    (state: any) => state?.userRoles?.currentRole || null,
  );
  const isRoleReady = Boolean(currentRole);
  const canManageApprovers =
    isRoleReady && hasRolePermission(currentRole, "manage_approvers");

  const fetchData = () => {
    mountedRef.current = true;
    setLoading(true);
    dispatch(setApiCall(true));
    // Run both calls in parallel
    Promise.allSettled([
      getApproverListApi().then((data) => {
        if (mountedRef.current) setApprovers(data);
      }),
      getAdminApi().then((data) => {
        if (mountedRef.current) {
          // Sort by plantId ascending before setting
          const sorted = Array.isArray(data)
            ? [...data].sort((a, b) => {
                const aId = a.plantId ?? "";
                const bId = b.plantId ?? "";
                // If both are numbers, compare numerically
                if (!isNaN(Number(aId)) && !isNaN(Number(bId))) {
                  return Number(aId) - Number(bId);
                }
                // Otherwise, compare as strings
                return String(aId).localeCompare(String(bId));
              })
            : data;
          setAdminList(sorted);
        }
      }),
      getPlantsApi().then((data) => {
        if (mountedRef.current) setPlants(data);
      }),
      getMaterialTypeApi().then((data) => {
        if (mountedRef.current) setMaterialTypes(data);
      }),
    ])
      .catch((err) => {
        // This catch is mostly a safety net; individual errors are already handled below if you want
        console.error("Fetch error:", err);
      })
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false);
          dispatch(setApiCall(false));
        }
      });

    return () => {
      // prevent state updates after unmount
      mountedRef.current = false;
    };
  };
  useEffect(() => {
    fetchData();

    return () => {
      // prevent state updates after unmount
      mountedRef.current = false;
    };
  }, []);
  //  Map API data to table rows
  useEffect(() => {
    const rows: AdminTableRow[] = adminList.map((admin, index) => {
      const email = admin.approverId || "";
      return {
        id: `${admin.approverId}_${index}`,
        __uid: `${admin.approverId}_${index}`,
        adminId: admin.adminId,
        Plant: admin.plantId || "",
        "IO Material Type": admin.ioMaterialType || "",
        "Planning Lead": admin.planningLead || "",
        Approver: admin.approverName,
        "Approver Email Address": email,
        "Updated By": admin.updatedBy || "-",
        "Updated Timestamp": admin.updatedTimestamp || "-",
      };
    });

    setTableRows(rows);
  }, [adminList, approvers]);

  const handleEditClick = (params: any) => {
    if (!canManageApprovers) return;
    setShowApproverInfo(true);
    // Enable edit mode
    params.node.setData({
      ...params.data,
      isEditing: true,
    });
    //  Focus email input
    requestAnimationFrame(() => {
      params.api.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "Approver",
      });
    });
  };

  const handleDeleteClick = (params: any) => {
    if (!canManageApprovers) return;
    setAction("delete");
    setSelectedRow(params.data);
    setOpenDialog(true);
  };

  const handleHistoryLogClick = async (row: AdminTableRow) => {
    // Cancel any in-flight request for a previous row
    if (historyAbortRef.current) {
      historyAbortRef.current.abort();
    }
    const controller = new AbortController();
    historyAbortRef.current = controller;

    setHistoryRow(row);
    setHistoryOpen(true);
    setHistoryLogs([]);

    if (!row?.adminId) {
      setHistoryLoading(false);
      return;
    }

    try {
      setHistoryLoading(true);
      const response = await getApproverHistoryById(row.adminId);

      // Ignore result if this request was superseded by a newer click
      if (controller.signal.aborted) return;
      const logs = Array.isArray(response) ? response : [];

      const mapped = logs.map((item: any, idx: number) => ({
        __uid: String(item?.id ?? `${row.adminId}-${idx}`),
        materialPlant: item?.materialPlant ?? "-",
        ioMaterialType: item?.ioMaterialType ?? "-",
        planningLead: item?.planningLead ?? "-",
        approverName: item?.approverName ?? "-",
        approverEmail: item?.approverEmail ?? "-",
        fromDate: item?.fromDate ?? "-",
        toDate: item?.toDate ?? "-",
        updatedBy: item?.updatedBy ?? "-",
        updatedTimestamp: item?.updatedTimestamp ?? "-",
      }));

      // Sort latest fromDate first; nulls/"-" go to bottom
      mapped.sort((a, b) => {
        const ta =
          a.fromDate && a.fromDate !== "-"
            ? new Date(a.fromDate).getTime()
            : -Infinity;
        const tb =
          b.fromDate && b.fromDate !== "-"
            ? new Date(b.fromDate).getTime()
            : -Infinity;
        return tb - ta;
      });

      setHistoryLogs(mapped);
    } catch (error: any) {
      console.error("Failed to load history log", error);
      setHistoryLogs([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const refreshActionColumn = (params: any) => {
    params.api.refreshCells({
      rowNodes: [params.node],
      columns: ["action"],
      force: true,
    });
  };
  // Sort approvers alphabetically by name (fallback to email if name missing)
  // Stable, null-safe, and recomputes only when `approvers` changes.
  const sortedApprovers = useMemo(() => {
    if (!Array.isArray(approvers)) return [];
    const clone = [...approvers];
    clone.sort((a, b) => {
      const nameA = (a?.name ?? a?.email ?? "").toLocaleLowerCase();
      const nameB = (b?.name ?? b?.email ?? "").toLocaleLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    return clone;
  }, [approvers]);
  //  Columns definition
  const columns: (ColDef | ColGroupDef)[] = useMemo(
    () => [
      {
        headerName: "Plant",
        field: "Plant",

        filterValueGetter: (params) => params.data?.Plant ?? null,

        //  CRITICAL: sorting must also be string-based
        comparator: (a, b) => String(a ?? "").localeCompare(String(b ?? "")),

        //  Ensure display stays exactly as stored (0124)
        valueFormatter: (params) => params.value ?? "-",

        editable: (params) => params.data?.isNew === true,
        cellEditor: (params) => (
          <DropdownEditor
            {...params}
            plants={plants}
            onSelect={setSelectedRow}
          />
        ),
        onCellValueChanged: refreshActionColumn,
        suppressKeyboardEvent: () => true,

        valueSetter: (params) => {
          const input = String(params.newValue ?? "").trim();
          if (!input) return false;

          // ✅ IMPORTANT: store as string, do NOT Number()
          params.data.Plant = input;
          return true;
        },
      },
      {
        headerName: "IO Material Type",
        field: "IO Material Type",
        editable: (params) => params.data?.isNew === true,
        cellEditor: (params) => (
          <DropdownEditor
            {...params}
            materialTypes={materialTypes}
            onSelect={setSelectedRow}
          />
        ),
        onCellValueChanged: refreshActionColumn,
        suppressKeyboardEvent: () => true,
        valueSetter: (params) => {
          const input = String(params.newValue ?? "").trim();
          if (!input) return false;
        },
      },
      {
        headerName: "Planning Lead",
        field: "Planning Lead",
        editable: (params) => params.data?.isNew === true,
        onCellValueChanged: refreshActionColumn,
      },
      {
        headerName: "Approver",
        field: "Approver",
        // Editable ONLY when user clicked Edit on an existing row
        editable: (params) =>
          params.data?.isNew === true || params.data?.isEditing === true,
        suppressKeyboardEvent: () => true,
        cellEditor: (params) => (
          <DropdownEditor
            {...params}
            approvers={sortedApprovers}
            onSelect={setSelectedRow}
          />
        ),
        valueSetter: (params) => {
          const input = String(params.newValue ?? "").trim();
          if (!input) return false;

          // Allow selection by either email OR name (robust to editor output)
          const matchByEmail = approvers.find((a) => a.email === input);
          const matchByName = approvers.find((a) => a.name === input);
          const chosen = matchByEmail ?? matchByName;

          const newEmail = chosen?.email ?? "";
          const newName = chosen?.name ?? input;

          // EXISTING ROW → trigger confirmation dialog (same as your email col)
          setSelectedRow({ ...params.data });
          setSelectedEmail(params.data["Approver Email Address"]);
          setAction("submit");
          setOpenDialog(true);
          return false; // block commit until Confirm
        },
      },
      {
        headerName: "Approver Email Address",
        field: "Approver Email Address",

        // Editable for:
        // - NEW rows
        // - EXISTING rows (only this column)
        // editable: (params) => params.data?.isEditing === true,

        suppressKeyboardEvent: () => true,
        cellEditor: (params) => (
          <DropdownEditor
            {...params}
            approvers={approvers}
            onSelect={setSelectedRow}
          />
        ),
        valueSetter: (params) => {
          const newEmail = params.newValue?.trim();
          if (!newEmail) return false;

          // EXISTING ROW → confirmation dialog
          setSelectedRow({ ...params.data });
          setSelectedEmail(params.data["Approver Email Address"]);
          setAction("submit");
          setOpenDialog(true);
          return false; //  prevent commit until confirm
        },
      },
      {
        headerName: "Updated By",
        field: "Updated By",
        editable: false,
      },
      {
        headerName: "Updated Timestamp",
        field: "Updated Timestamp",
        editable: false,
      },
      {
        headerName: "Action",
        field: "action",
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        cellRenderer: (params: any) => {
          const row = params.data;

          // NEW ROW → Save & Close
          if (row.isNew) {
            return (
              <>
                <IconButton
                  title="Save"
                  size="small"
                  sx={{
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  }}
                  onClick={() => handleTickClick(row)}
                >
                  <SaveIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  title="Cancel"
                  size="small"
                  sx={{
                    "&:hover": {
                      color: theme.palette.error.main,
                    },
                  }}
                  onClick={() => {
                    // Remove the new row from the table
                    setTableRows((prev) => prev.filter((r) => r.id !== row.id));
                    setShowApproverInfo(false);
                    if (gridRef.current) {
                      const node = gridRef.current.api.getRowNode(row.id);
                      if (node) {
                        gridRef.current.api.applyTransaction({ remove: [row] });
                      }
                    }
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </>
            );
          }

          // EXISTING ROW → Edit
          return (
            <>
              {canManageApprovers && (
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(params)}
                  title="Edit"
                  sx={{
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              )}
              {canManageApprovers && (
                <IconButton
                  title="Delete"
                  size="small"
                  onClick={() => handleDeleteClick(params)}
                  sx={{
                    "&:hover": {
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              )}
              <IconButton
                title="History Log"
                size="small"
                onClick={() => handleHistoryLogClick(row)}
                sx={{
                  "&:hover": {
                    color: theme.palette.info.main,
                  },
                }}
              >
                <HistoryIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </>
          );
        },
      },
    ],
    [
      approvers,
      materialTypes,
      plants,
      sortedApprovers,
      canManageApprovers,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.primary.main,
    ],
  );

  // Add Approver
  const handleAddApprover = () => {
    const newId = `new_${newRowIdRef.current++}`;
    if (!canManageApprovers) return;
    setShowApproverInfo(true);
    const emptyRow: AdminTableRow = {
      id: newId,
      Plant: "",
      "IO Material Type": "",
      "Planning Lead": "",
      Approver: "",
      "Approver Email Address": "",
      isNew: true,
    };
    setTableRows((prev) => [emptyRow, ...prev]);

    if (gridRef.current) {
      // Clear all filters so the new row is always visible
      gridRef.current.api.setFilterModel(null);
      gridRef.current.api.applyTransaction({
        add: [emptyRow],
        addIndex: gridRef.current.api.getDisplayedRowCount(), // add to end
      });

      // Use setTimeout 0 to ensure grid has rendered the row
      setTimeout(() => {
        const rowIndex = gridRef.current.api.getDisplayedRowCount() - 1;
        gridRef.current.api.startEditingCell({
          rowIndex,
          colKey: "Plant", // first column
        });
        gridRef.current.api.ensureIndexVisible(0, "top");
      }, 0);
    }
  };

  const handleResetFilters = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    setIsTriggerFilter(true);
    api.onFilterChanged();
  };

  // Tick mark clicked
  const handleTickClick = (rowData: AdminTableRow) => {
    if (!canManageApprovers) return;
    setSelectedRow(rowData);
    setSelectedEmail(rowData["Approver Email Address"]);
    setOpenDialog(true);
    setAction("submit");
  };

  // Cancel dialog
  const handleCancel = () => setOpenDialog(false);

  const toNullIfEmpty = (value: any) =>
    value === "" || value === undefined ? null : value;

  const refreshAdminTable = async () => {
    const refreshedData = await getAdminApi();
    if (mountedRef.current) {
      // Sort by plantId ascending before setting
      const sorted = Array.isArray(refreshedData)
        ? [...refreshedData].sort((a, b) => {
            const aId = a.plantId ?? "";
            const bId = b.plantId ?? "";
            if (!isNaN(Number(aId)) && !isNaN(Number(bId))) {
              return Number(aId) - Number(bId);
            }
            return String(aId).localeCompare(String(bId));
          })
        : refreshedData;
      setAdminList(sorted);
    }
  };

  // Confirm dialog → submit API
  const handleConfirm = async () => {
    if (!canManageApprovers) {
      setOpenDialog(false);
      return;
    }

    if (!selectedRow) {
      setOpenDialog(false);
      return;
    }

    const email = selectedRow["Approver Email Address"];
    const primary = accounts?.[0] as any;
    const updatedBy =
      primary?.idTokenClaims?.preferred_username ||
      primary?.username ||
      primary?.idTokenClaims?.email ||
      "";
    console.log("email", selectedRow);
    const isAddAction = selectedRow.isNew === true;
    const actionType: AdminRequestPayload["action"] =
      action === "delete" ? "DELETE" : isAddAction ? "ADD" : "UPDATE";
    const requestBody: AdminRequestPayload = {
      plantId: toNullIfEmpty(selectedRow.Plant),
      ioMaterialType: selectedRow["IO Material Type"],
      planningLead: toNullIfEmpty(selectedRow["Planning Lead"]),
      approverEmail: email,
      approverName:
        selectedRow["Approver"] ||
        approvers.find((a) => a.email === email)?.name ||
        "",
      updatedBy,
      action: actionType,
    };

    try {
      if (action === "submit") {
        const response = await submitAdminApi(requestBody);

        // Dynamic success message
        setToastSeverity("success");
        setToastMessage(response?.message || "Approver saved successfully!");
        setToastOpen(true);

        // Re-sync grid with backend after add/update to avoid UI discrepancy.
        await refreshAdminTable();
      } else {
        const res = await deleteAdminApi(requestBody);
        if (res.status === 200 && res.data.includes("Successfully")) {
          setToastSeverity("success");
          setToastMessage(res.data);
          setToastOpen(true);
          setTableRows((prev) => prev.filter((r) => r.id !== selectedRow.id));
        } else {
          setToastSeverity("error");
          setToastMessage(res.data);
          setToastOpen(true);
        }
      }

      setOpenDialog(false);
      setSelectedRow(null);
      setSelectedEmail(null);
    } catch (error: any) {
      console.error("API error:", error);

      // Dynamic error message
      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message ??
        error?.response?.data ??
        "Failed to save approver",
      );
      setToastOpen(true);

      // For add/update failure, re-sync to remove temporary/new stale UI rows.
      if (action === "submit") {
        try {
          await refreshAdminTable();
        } catch (refreshError) {
          console.error("Refresh after submit failure failed:", refreshError);
        }
      }

      setOpenDialog(false);
    }
  };

  const handleCloseSubmitToast = () => setToastOpen(false);

  const normalize = (val: any) =>
    val === null || val === undefined || String(val).trim() === ""
      ? ""
      : String(val).trim();

  const validateUploadRow = (row: any, rowIndex: number) => {
    const errors: string[] = [];

    const ioMaterialType = normalize(row["IO Material Type"]);
    const email = normalize(row["Approver Email Address"]);
    const plant = normalize(row["Plant"]);
    const planningLead = normalize(row["Planning Lead"]);

    // 1. IO Material Type required
    if (!ioMaterialType) {
      errors.push("IO Material Type is required");
    }

    // 2. Approver Email required
    if (!email) {
      errors.push("Approver Email Address is required");
    }

    // 3. Either Plant OR Planning Lead
    if (!plant && !planningLead) {
      errors.push("Either Plant or Planning Lead must be provided");
    }

    // 4. Email already exists on screen
    const emailAlreadyExists =
      Array.isArray(tableRows) &&
      tableRows.some(
        (r) =>
          normalize(r["Approver Email Address"]).toLowerCase() ===
          email.toLowerCase(),
      );

    if (emailAlreadyExists) {
      errors.push("Approver Email Address already exists on screen");
    }

    return {
      isValid: errors.length === 0,
      errors,
      rowNumber: rowIndex + 2, // header + 1-based index
    };
  };

  const handleUpload = async (uploadPayload) => {
    if (!uploadPayload.length) return;
    try {
      setSubmitting(true);
      await submitAdminBulkApi(uploadPayload);

      setToastSeverity("success");
      setToastMessage(
        `${uploadPayload.length} approvers uploaded successfully`,
      );
      setToastOpen(true);

      // Refresh grid
      const refreshedData = await getAdminApi();
      setAdminList(refreshedData);
      setUploadPayload([]);
      setUploadOpen(false);
    } catch (error: any) {
      console.error("Upload API failed", error);

      setToastSeverity("error");
      setToastMessage(
        error?.response?.data?.message || "Upload failed for some records",
      );
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ paddingLeft: 0, flex: 1 }}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              textAlign: "left",
              marginLeft: 1,
            }}
          >
            Approver Maintenance View
          </Typography>

          {/* Right-side controls */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* <ButtonComponent
              className="take-action"
              type="secondary"
              label="Upload"
              onClick={ () => setUploadOpen(true) }
              borderRadius="28px"
            /> */}
            {canManageApprovers && (
              <ButtonComponent
                className="take-action"
                type="secondary"
                label="Add Approver"
                onClick={handleAddApprover}
                borderRadius="28px"
              />
            )}
            {(tableRows?.length ?? 0) > 0 && (
              <><ButtonComponent
                className="take-action"
                type="secondary"
                label="Reset Filters"
                onClick={handleResetFilters}
                borderRadius="28px"
                width="auto"
                maxWidth="none"
              /><FilterManagerButton
                  gridRef={gridRef}
                  tableName="Approver Maintenance View"
                  userEmail={localStorage.getItem("userEmail") || ""}
                  disabled={!tableRows?.length}
                /><BulkPasteFilter
                  gridRef={gridRef}
                  columns={columns}
                  setIsTriggerFilter={setIsTriggerFilter}
                  triggerClearFilter = {isTriggerFilter}
                /></>
            )}
            <Download
              loading={loading}
              gridRef={gridRef}
              fileName="Approver Maintenance View"
              rowData={tableRows}
            />
          </Stack>
        </Stack>

        {/* Approver source info banner — shown only after Add / Edit click */}
        {/* {showApproverInfo && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{
              py: 0.5,
              px: 1.5,
              borderRadius: 1,
              fontSize: 13,
              alignItems: "center",
              "& .MuiAlert-message": { py: 0.25 },
            }}
          >
            Approver names are sourced from the AD group. If an approver does not appear in the dropdown, they have not yet been added to the AD group — please request access from User Access Management.
          </Alert>
        )} */}

        {/* Confirmation Dialog */}
        <Dialog open={openDialog} onClose={handleCancel}>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to {action} this approver?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirm} color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <HistoryLogDialog
          open={historyOpen}
          onClose={() => {
            setHistoryOpen(false);
            if (historyAbortRef.current) {
              historyAbortRef.current.abort();
              historyAbortRef.current = null;
            }
          }}
          row={historyRow}
          loading={historyLoading}
          logs={historyLogs}
        />

        {/* Error Dialog (centered) */}
        <ErrorDialog
          open={toastOpen && toastSeverity === "error"}
          message={toastMessage}
          onClose={handleCloseSubmitToast}
        />

        {/* Success/Info Toast */}
        <Snackbar
          open={toastOpen && toastSeverity !== "error"}
          autoHideDuration={3000}
          onClose={handleCloseSubmitToast}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSubmitToast}
            severity={toastSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>

        {/* AG Grid Table */}
        <RenderTable
          loading={loading}
          gridRef={gridRef}
          gridTheme={gridTheme}
          columns={columns}
          rows={tableRows}
          rowsPerPage={10}
          setGridReady={() => { }}
          sideBar={{}}
          minimized={null}
          emitEvent={() => { }}
          onChange={() => { }}
          isSelectedRow={false}
          setSelectedRow={(rowData) => setSelectedRow(rowData)}
        />

        {/* <Box paddingBottom={1} />
        <UploadDialogBox
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUpload={handleUpload}
          title="Upload"
          submitting={submitting}
          description="Download the template (optional), then choose your file and upload."
          templateHeaders={ADMIN_TEMPLATE_HEADERS}
           dedupeBy={["Plant", "IO Material Type", "Approver Email Address"]}
          templateFileName="ApproverUploadTemplate.xlsx"
          tableRows={tableRows}
          setTableRows={setTableRows}
          approvers={approvers}
          setAdminList={setAdminList}
          setToastMessage={setToastMessage}
          setToastSeverity={setToastSeverity}
          setToastOpen={setToastOpen}
          validateRow={validateUploadRow}
          uploadPayload={uploadPayload}
          setUploadPayload={setUploadPayload}
        /> */}
      </Stack>
    </ThemeProvider>
  );
};
